/**
 * Which of the rep's assigned products belong on a meeting. Pure and Angular-free
 * so the rule can be checked directly.
 *
 * The list a position is assigned carries more than the brands a rep details:
 * promotional materials, samples and SKUs sit in the same table. Anywhere the rep
 * is asked which product a conversation was about, only the brands belong.
 */

/** Product type given to promotional material: brochures, leaflets, branded items. */
export const PROMOTIONAL_PRODUCT_TYPE = 268890001;

/** Product applicability value meaning the product belongs on a meeting. */
export const MEETING_APPLICABILITY = '100000000';

/** The fields the rule reads; the real {@link Brand} class satisfies it. */
export interface MeetingBrandFields {
  producttype?: number | null;
  indskr_producttype?: number | null;
  productApplicability?: string[] | null;
}

/**
 * Whether this product belongs on a meeting.
 *
 * The product type is read under both names it arrives with: the products endpoint
 * supplies one or the other depending on the environment, and a rule reading only
 * one of them silently passes everything where that one is absent.
 *
 * Applicability left unset means the product is not restricted, which is how the
 * rest of the app reads it.
 */
export function isMeetingBrand(brand: MeetingBrandFields | null | undefined): boolean {
  if (!brand) {
    return false;
  }
  if (brand.producttype === PROMOTIONAL_PRODUCT_TYPE || brand.indskr_producttype === PROMOTIONAL_PRODUCT_TYPE) {
    return false;
  }
  const applicability = brand.productApplicability ?? [];
  return !applicability.length || applicability.includes(MEETING_APPLICABILITY);
}

/** The products from this list that belong on a meeting. */
export function meetingBrands<T extends MeetingBrandFields>(brands: T[] | null | undefined): T[] {
  return (brands ?? []).filter(isMeetingBrand);
}
